import styled, { css } from 'styled-components';
import themeColors, { baseColors } from 'styles/config/colors';
import themeSpacing from 'styles/config/spacing';
import text from 'styles/config/text';

import { CloseIcon } from '../Icons';
import { Subtitle } from '../LegacyText';
import type { CalloutOverrides } from '.';
import { CalloutType } from '.';

export type BorderStylesProps = {
  $withBorder?: boolean;
  $rounded?: boolean;
  $dark?: boolean;
};
export const BorderStyles = css<
  BorderStylesProps & {
    $type?: CalloutType;
    $overrides?: Partial<CalloutOverrides>;
  }
>`
  ${({ $rounded }) => {
    return css`
      border-radius: ${$rounded ? themeSpacing.radiusXSmall : themeSpacing.radiusMedium};
    `;
  }}

  ${({ $type, $overrides, $withBorder, $dark }) => {
    if (!$withBorder) {
      return;
    }
    if ($overrides?.borderStyle) {
      return css`
        border: ${$overrides.borderStyle};
      `;
    }
    switch ($type) {
      case CalloutType.INFO:
        return $dark
          ? css`
              border: 1px solid ${baseColors.blue['50']};
            `
          : css`
              border: 1px solid ${baseColors.blue['20']};
            `;
      case CalloutType.ERROR:
        return $dark
          ? css`
              border: 1px solid ${baseColors.red['50']};
            `
          : css`
              border: 1px solid ${baseColors.red['20']};
            `;
      case CalloutType.WARNING:
        return $dark
          ? css`
              border: 1px solid ${baseColors.yellow['50']};
            `
          : css`
              border: 1px solid ${baseColors.yellow['20']};
            `;
      case CalloutType.SUCCESS:
        return $dark
          ? css`
              border: 1px solid ${baseColors.green['50']};
            `
          : css`
              border: 1px solid ${baseColors.green['20']};
            `;
    }
  }}
`;

export const CalloutWrapper = styled.div<
  BorderStylesProps & {
    $type?: CalloutType;
    $overrides?: Partial<CalloutOverrides>;
    $iconOnTop?: boolean;
  }
>`
  display: flex;
  gap: ${themeSpacing.xs3};
  width: 100%;
  ${({ $iconOnTop = false }) =>
    !$iconOnTop &&
    css`
      align-items: center;
    `}
  padding: ${themeSpacing.xs3} ${themeSpacing.sm1};

  ${BorderStyles}

  ${({ $type, $dark }) => {
    switch ($type) {
      case CalloutType.INFO:
        return $dark
          ? css`
              color: ${baseColors.blue['10']};
              background: ${baseColors.blue['90']};
            `
          : css`
              color: ${themeColors.textInfo};
              background: ${baseColors.blue['05']};
            `;
      case CalloutType.ERROR:
        return $dark
          ? css`
              color: ${baseColors.red['10']};
              background: ${baseColors.red['90']};
            `
          : css`
              color: ${themeColors.errorText01};
              background: ${baseColors.red['05']};
            `;
      case CalloutType.WARNING:
        return $dark
          ? css`
              color: ${baseColors.yellow['10']};
              background: ${baseColors.yellow['100']};
            `
          : css`
              color: ${themeColors.textWarning};
              background: ${baseColors.yellow['05']};
            `;
      case CalloutType.SUCCESS:
        return $dark
          ? css`
              color: ${baseColors.green['10']};
              background: ${baseColors.green['90']};
            `
          : css`
              color: ${themeColors.textSuccess};
              background: ${baseColors.green['05']};
            `;
      case CalloutType.NEUTRAL:
        return $dark
          ? css`
              color: ${baseColors.grey['10']};
              background: ${baseColors.grey['90']};
            `
          : css`
              color: ${themeColors.textPrimary};
              background: ${baseColors.grey['05']};
            `;
      default:
        return css`
          color: ${themeColors.textSecondary};
          background: ${themeColors.backgroundPage};
        `;
    }
  }}

  ${({ $overrides }) => {
    if ($overrides?.background) {
      return css`
        background: ${$overrides.background};
      `;
    }
  }}
  ${({ $overrides }) => {
    if ($overrides?.color) {
      return css`
        color: ${$overrides.color};
      `;
    }
  }}
`;

export const CalloutIconContainer = styled.div`
  display: flex;
  align-items: start;
  height: 100%;
`;

export const CalloutContentWrapper = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: space-between;
  gap: ${themeSpacing.xs2};
`;

export const CalloutContent = styled.div`
  display: flex;
  gap: ${themeSpacing.xs2};
  align-items: center;
  width: 100%;
`;

export const CalloutAction = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${themeSpacing.xs1};
`;

export const CalloutHeadline = styled(Subtitle)`
  font-size: ${text.size.header.regular};
  font-weight: ${text.weight.semibold};
`;

export const CloseButton = styled(CloseIcon)`
  cursor: pointer;
  display: inline-flex;
`;
